import { useMemo } from "react";
import { APPS_PAGE_SIZE } from "@zapier/utils";
import { useQueryPublicApps } from "./useQueryPublicApps";
import { useQueryPrivateApps } from "./useQueryPrivateApps";
import { useHydrateAppsWithImplementationsMeta } from "./useHydrateAppsWithImplementationsMeta";
import { useHydrateAppsWithStats } from "./useHydrateAppsWithStats";
import { AppsOrder } from "@zapier/bff";
import { useLimitOffsetFromPage } from "../../hooks/useLimitOffsetFromPage";
interface Input {
  /**
   * `latest_public` and `other` fetch a single source. `all` fetches and
   * merges both, for views where no version filter is applied — otherwise
   * private apps would be silently hidden from an unfiltered list (CRIT-3021).
   */
  filterType?: "latest_public" | "other" | "all";
  order?: AppsOrder;
  page?: number | null;
  pageSize?: number;
  search?: string;
}

export function useApps({
  filterType = "latest_public",
  order = "title",
  page = 1,
  pageSize = APPS_PAGE_SIZE,
  search,
}: Input) {
  const { limit, offset } = useLimitOffsetFromPage(page ?? 1, pageSize);

  // Call both hooks unconditionally
  const publicAppsQuery = useQueryPublicApps(
    {
      limit,
      offset,
      ordering: order,
      search,
    },
    { enabled: filterType !== "other" },
  );

  const privateAppsQuery = useQueryPrivateApps(
    {
      limit,
      offset,
      ordering: order,
      search,
    },
    { enabled: filterType !== "latest_public" },
  );

  // Choose which result to use based on type
  const isMergedView = filterType === "all";
  const activeQuery =
    filterType === "other" ? privateAppsQuery : publicAppsQuery;

  const apps = useMemo(
    () =>
      isMergedView
        ? [...publicAppsQuery.apps, ...privateAppsQuery.apps]
        : activeQuery.apps,
    [
      isMergedView,
      publicAppsQuery.apps,
      privateAppsQuery.apps,
      activeQuery.apps,
    ],
  );
  const totalCount = isMergedView
    ? publicAppsQuery.totalCount + privateAppsQuery.totalCount
    : activeQuery.totalCount;
  const isLoading = isMergedView
    ? publicAppsQuery.isLoading || privateAppsQuery.isLoading
    : activeQuery.isLoading;
  const isFetching = isMergedView
    ? publicAppsQuery.isFetching || privateAppsQuery.isFetching
    : activeQuery.isFetching;

  /** Hydrate auths with app implementation details *********************************************** */

  const {
    apps: hydratedAuthsWithImplementationDetails,
    isFetching: isHydratingAppsWithImplementationsMetaFetching,
    isLoading: isHydratingAppsWithImplementationsMetaLoading,
  } = useHydrateAppsWithImplementationsMeta({ apps });

  /** Hydrate app stats  *********************************************** */

  const {
    apps: hydratedAppsWithStats,
    isFetching: isHydratingAppsWithStatsFetching,
    isLoading: isHydratingAppsWithStatsLoading,
  } = useHydrateAppsWithStats({
    apps: hydratedAuthsWithImplementationDetails,
  });

  const totalPages = Math.ceil(totalCount / pageSize);

  return {
    hydratedApps: hydratedAppsWithStats,
    isFetching:
      isFetching ||
      isHydratingAppsWithImplementationsMetaFetching ||
      isHydratingAppsWithStatsFetching,
    isLoading:
      isLoading ||
      isHydratingAppsWithImplementationsMetaLoading ||
      isHydratingAppsWithStatsLoading,
    totalCount,
    totalPages,
  };
}
